"use client"

import { motion } from "framer-motion"
import clsx from "clsx"
import styles from "./SignupSuccessOverlay.module.css"

export const PHRASES = [
  "Incepting your account",
  "Configuring awesomeness",
  "Cracking knuckles",
  "Getting ready for WOW",
  "Crunching quota",
  "Waking up Joe Test",
  "Remembering Glengarry",
  "Getting in the zone",
  "Resetting the hustlemeter",
  "Caffeinating the server",
  "Firing up the droids",
  "Counting down to done",
  "Reticulating splines",
  "Calling the banners",
  "Booting up the bots",
  "Haranguing the troops",
  "Pregaming action",
] as const

// Number of visible items at once
export const VISIBLE_ITEMS = 5
// Position of the active item (0-indexed)
export const ACTIVE_ITEM_POSITION = 2
// Time between advancing items (in milliseconds)
export const ITEM_DURATION = 2000
// Height of each item (in pixels)
export const ITEM_HEIGHT = 50

export function shuffleArray<T>(array: T[]): T[] {
  const newArray = [...array]
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[newArray[i], newArray[j]] = [newArray[j], newArray[i]]
  }
  return newArray
}

interface CheckmarkSVGProps {
  isDrawing: boolean
  isComplete: boolean
}

function CheckmarkSVG({ isDrawing, isComplete }: CheckmarkSVGProps) {
  const pathVariants = {
    hidden: { pathLength: 0 },
    visible: { pathLength: 1 },
    predrawn: { pathLength: 1 },
  }

  return (
    <g>
      <motion.circle
        cx={12}
        cy={12}
        r={11}
        stroke="white"
        strokeWidth={2}
        fill="transparent"
        variants={pathVariants}
        initial={isComplete ? "predrawn" : "hidden"}
        animate="visible"
        transition={{
          duration: isComplete ? 0 : 0.4,
          ease: "easeOut",
        }}
      />
      <motion.path
        d="M7.5 12.5L10.5 15.5L16.5 9.5"
        stroke="white"
        strokeWidth={2}
        fill="transparent"
        variants={pathVariants}
        initial={isComplete ? "predrawn" : "hidden"}
        animate="visible"
        transition={{
          duration: isComplete ? 0 : 0.3,
          delay: isComplete ? 0 : 0.4,
          ease: "easeOut",
        }}
      />
    </g>
  )
}

interface PhraseItemProps {
  phrase: string
  visualPosition: number
  isActive: boolean
  isComplete: boolean
  progress: number
}

export function PhraseItem({ phrase, visualPosition, isActive, isComplete, progress }: PhraseItemProps) {
  // Calculate the y position with smooth transition using progress (0-1)
  const y = (visualPosition - progress) * ITEM_HEIGHT

  // Calculate opacity based on distance from center
  const distanceFromCenter = Math.abs(visualPosition - progress - ACTIVE_ITEM_POSITION)
  const opacity = distanceFromCenter >= 2 ? 0 : distanceFromCenter <= 0.5 ? 1 : 1 - (distanceFromCenter - 0.5) * 0.7

  return (
    <motion.div
      className={clsx(styles.phraseContainer, "prevent-select")}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        opacity,
        y,
        transition: "opacity 0.3s ease",
      }}
    >
      <span className={styles.text}>{phrase}</span>
      {(isActive || isComplete) && (
        <motion.svg
          width="24"
          height="24"
          style={{
            position: "absolute",
            left: 10,
          }}
        >
          <CheckmarkSVG isDrawing={isActive} isComplete={isComplete} />
        </motion.svg>
      )}
    </motion.div>
  )
}
